import React, { useCallback, useMemo, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { EntityNode } from '../nodes/EntityNode';
import { RelationshipEdge } from '../edges/RelationshipEdge';
import { GraphControls } from '../controls/GraphControls';
import { GraphMinimap } from '../controls/GraphMinimap';
import type { GraphCanvasProps, GraphEntityNode, BackgroundPattern } from '../types';
import './GraphCanvas.scss';

const nodeTypes = { entity: EntityNode };
const edgeTypes = { relationship: RelationshipEdge };

const BACKGROUND_MAP: Record<Exclude<BackgroundPattern, 'none'>, BackgroundVariant> = {
  dots: BackgroundVariant.Dots,
  lines: BackgroundVariant.Lines,
  cross: BackgroundVariant.Cross,
};

const GraphCanvasInner: React.FC<GraphCanvasProps> = ({
  nodes: initialNodes,
  edges: initialEdges,
  nodeConfigs,
  onNodeSelect,
  onNodeDoubleClick,
  fitView = true,
  className,
  backgroundPattern = 'none',
  showControls = true,
  showMinimap = false,
  interactive = true,
}) => {
  const nodesWithConfig = useMemo(
    () =>
      initialNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          config: nodeConfigs[node.data.entityType],
        },
      })),
    [initialNodes, nodeConfigs]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(nodesWithConfig as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges as Edge[]);

  useEffect(() => {
    setNodes(nodesWithConfig as Node[]);
  }, [nodesWithConfig, setNodes]);

  useEffect(() => {
    setEdges(initialEdges as Edge[]);
  }, [initialEdges, setEdges]);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      onNodeSelect?.(node as GraphEntityNode);
    },
    [onNodeSelect]
  );

  const handlePaneClick = useCallback(() => {
    onNodeSelect?.(null);
  }, [onNodeSelect]);

  const handleNodeDoubleClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      onNodeDoubleClick?.(node as GraphEntityNode);
    },
    [onNodeDoubleClick]
  );

  return (
    <div className={`graph-canvas ${className || ''}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={interactive ? onNodesChange : undefined}
        onEdgesChange={interactive ? onEdgesChange : undefined}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView={fitView}
        nodesDraggable={interactive}
        nodesConnectable={false}
        elementsSelectable={interactive}
        panOnDrag={interactive}
        zoomOnScroll={interactive}
        proOptions={{ hideAttribution: true }}
      >
        {backgroundPattern !== 'none' && (
          <Background
            variant={BACKGROUND_MAP[backgroundPattern]}
            gap={20}
            size={1}
          />
        )}
        {showControls && <GraphControls />}
        {showMinimap && <GraphMinimap />}
      </ReactFlow>
    </div>
  );
};

export const GraphCanvas: React.FC<GraphCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <GraphCanvasInner {...props} />
    </ReactFlowProvider>
  );
};



